import { useEffect, useState } from 'react';

import {
  Tier,
  TIER_TO_TRACK_CODE,
  TIER_TO_CAREER_TRACK_NAME,
  TRACK_CODE_ORDER,
  TRACK_CODE_TO_NAME,
  CareerTrackRadio,
  InfoModal,
} from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import { Box, SxProps, Theme, Typography } from '@mui/material';

const STRINGS = appStrings.modules.survey.output.blueprintSection.minimumTrack.careerTracksModal;

const DIAGRAM_STRINGS = appStrings.modules.results.diagram;

/** Modal display order: highest tier to lowest (reverses the canonical TRACK_CODE_ORDER) */
const MODAL_TIER_ORDER: Tier[] = [...TRACK_CODE_ORDER]
  .reverse()
  .map((code) => TRACK_CODE_TO_NAME[code]);

type CareerTracksModalProps = Readonly<{
  open: boolean;
  onClose: () => void;
  initialTier?: Tier;
}>;

export function CareerTracksModal({ open, onClose, initialTier }: CareerTracksModalProps) {
  const [expandedTier, setExpandedTier] = useState<Tier | null>(initialTier ?? null);

  useEffect(() => {
    if (open) {
      setExpandedTier(initialTier ?? null);
    }
  }, [open, initialTier]);

  const handleToggle = (tier: Tier) => {
    setExpandedTier((prev) => (prev === tier ? null : tier));
  };

  return (
    <InfoModal
      open={open}
      onClose={onClose}
      width="sm"
      icon="AnnotationInfo"
      hideCircleImage
      headerContent={<Typography variant="display-xs-semibold">{STRINGS.title}</Typography>}
      bodyContent={
        <Box sx={listSx}>
          {MODAL_TIER_ORDER.map((tier) => {
            const isExpanded = expandedTier === tier;
            const description = DIAGRAM_STRINGS[tier]?.tooltip ?? '';

            return (
              <Box
                key={tier}
                id={`career-tracks-modal-row-${TIER_TO_TRACK_CODE[tier]}`}
                sx={rowSx(isExpanded)}
                onClick={() => handleToggle(tier)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleToggle(tier);
                  }
                }}
                role="button"
                tabIndex={0}
                aria-expanded={isExpanded}
              >
                <Box sx={rowHeaderSx}>
                  <CareerTrackRadio careerTrack={TIER_TO_CAREER_TRACK_NAME[tier]} width={24} />
                  <Typography variant="text-md-medium">{tier}</Typography>
                </Box>
                <Box sx={descriptionContainerSx(isExpanded)}>
                  <Typography variant="text-sm-regular" sx={descriptionTextSx}>
                    {description}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      }
    />
  );
}

// --- Styles ---

const listSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
});

const rowSx =
  (isExpanded: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: backrs.spacing['spacing-md'],
    padding: `${backrs.spacing['spacing-lg']} ${backrs.spacing['spacing-xl']}`,
    borderRadius: backrs.radius['radius-lg'],
    border: `1px solid ${backrs.colors[isExpanded ? 'Brand/500' : 'Colors/Border/border-secondary']}`,
    cursor: 'pointer',
  });

const rowHeaderSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
});

const descriptionContainerSx =
  (isExpanded: boolean): SxProps<Theme> =>
  () => ({
    display: 'grid',
    gridTemplateRows: isExpanded ? '1fr' : '0fr',
    opacity: isExpanded ? 1 : 0,
    transition: 'grid-template-rows 0.3s ease-in-out, opacity 0.3s ease-in-out',
    '& > *': {
      overflow: 'hidden',
    },
  });

const descriptionTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});
